<template>
  <div class="wellness-cal">
    <div class="cal-header">
      <h3 class="cal-title">Mood &amp; wellness diary</h3>
      <div class="cal-nav">
        <button type="button" class="cal-nav-button" @click="changeMonth(-1)" aria-label="Previous month">
          <i class="bi bi-chevron-left" aria-hidden="true"></i>
        </button>
        <span class="cal-month">{{ monthLabel }}</span>
        <button type="button" class="cal-nav-button" @click="changeMonth(1)" aria-label="Next month">
          <i class="bi bi-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <div class="cal-grid">
      <div class="cal-dow" v-for="dow in dayHeaders" :key="dow">{{ dow }}</div>

      <div v-for="cell in calendarCells" :key="cell.key" class="cal-cell" :class="{
        'cal-cell--blank': !cell.inMonth,
        'cal-cell--today': cell.inMonth && cell.isToday,
        'cal-cell--has-data': cell.inMonth && cell.entry !== null,
        'cal-cell--readonly': cell.inMonth && cell.entry === null && !canAdd,
      }" :tabindex="isCellInteractive(cell) ? 0 : -1" :role="isCellInteractive(cell) ? 'button' : null"
        @click="onCellClick(cell)" @keydown.enter.prevent="onCellClick(cell)"
        @keydown.space.prevent="onCellClick(cell)">
        <template v-if="cell.inMonth">
          <span class="cal-date">{{ cell.dateNum }}</span>
          <span v-if="cell.entry !== null" class="cal-score" :class="`score--${scoreLevel(cell.average)}`">
            {{ cell.average }}
          </span>
          <span v-else-if="canAdd" class="cal-add" aria-hidden="true"><i class="bi bi-plus"></i></span>
        </template>
      </div>
    </div>

    <div class="cal-legend">
      <span class="legend-chip score--good">7–10 feeling good</span>
      <span class="legend-chip score--mid">4–6.9 okay</span>
      <span class="legend-chip score--low">0–3.9 struggling</span>
      <span class="legend-note">average of the day's five scores</span>
    </div>

    <!-- Read-only day detail -->
    <b-modal v-if="showDayDetail" v-model="showDayDetail" size="md" :title="dayDetailTitle" hide-footer>
      <ul class="day-scores" v-if="selectedEntry !== null">
        <li v-for="metric in metrics" :key="metric.key" class="day-score">
          <span class="day-score__label">{{ metric.label }}</span>
          <span class="day-score__bar">
            <span class="day-score__fill" :class="`fill--${scoreLevel(metricValue(metric.key))}`"
              :style="{ width: metricValue(metric.key) * 10 + '%' }"></span>
          </span>
          <span class="day-score__value">{{ metricValue(metric.key) }}/10</span>
        </li>
      </ul>
    </b-modal>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { normaliseEntry, entryAverage, bandForTen, TEN_SCALE } from "@/utils/wellnessScale";

export default {
  name: "WellnessCalendar",
  props: {
    // Season mood entries: [{ date: "YYYY-MM-DD", sleep, happyness, stress,
    // fatigue, muscle_soreness, scale_max, ... }], higher is better. Raw values
    // are on the client's own scale (web 1–7, Flutter 1–5); everything shown
    // here is converted to 0–10 via utils/wellnessScale so the legend bands
    // mean the same thing whichever app the entry came from.
    moodData: {
      type: Array,
      default: () => [],
    },
    canAdd: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["add-day"],
  data() {
    return {
      dayHeaders: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      year: dayjs().year(),
      month: dayjs().month(),
      showDayDetail: false,
      selectedEntry: null,
      metrics: [
        { key: "sleep", label: "Sleep" },
        { key: "happyness", label: "Happiness" },
        { key: "stress", label: "Stress" },
        { key: "fatigue", label: "Fatigue" },
        { key: "muscle_soreness", label: "Muscle soreness" },
      ],
    };
  },
  computed: {
    monthLabel() {
      return dayjs().year(this.year).month(this.month).format("MMMM YYYY");
    },
    entriesByDate() {
      const map = new Map();
      const entries = Array.isArray(this.moodData) ? this.moodData : [];
      entries.forEach((entry) => {
        map.set(dayjs(entry.date).format("YYYY-MM-DD"), entry);
      });
      return map;
    },
    calendarCells() {
      const monthStart = dayjs().year(this.year).month(this.month).date(1);
      const daysInMonth = monthStart.daysInMonth();
      const cells = [];

      const leading = (monthStart.day() + 6) % 7; // Mon = 0
      for (let i = 0; i < leading; i++) {
        cells.push({ inMonth: false, key: `lead-${i}` });
      }

      for (let dayNum = 1; dayNum <= daysInMonth; dayNum++) {
        const date = monthStart.date(dayNum);
        const key = date.format("YYYY-MM-DD");
        const entry = this.entriesByDate.get(key) || null;
        cells.push({
          inMonth: true,
          key,
          date,
          dateNum: dayNum,
          isToday: date.isSame(dayjs(), "day"),
          entry,
          average: entry !== null ? this.entryAverage(entry) : null,
        });
      }

      while (cells.length % 7 !== 0) {
        cells.push({ inMonth: false, key: `tail-${cells.length}` });
      }
      return cells;
    },
    dayDetailTitle() {
      return this.selectedEntry !== null
        ? `Wellness — ${dayjs(this.selectedEntry.date).format("ddd D MMM YYYY")}`
        : "Wellness";
    },
  },
  methods: {
    entryAverage(entry) {
      return entryAverage(entry, TEN_SCALE) ?? 0;
    },
    scoreLevel(score) {
      return bandForTen(score);
    },
    metricValue(key) {
      if (this.selectedEntry === null) return 0;
      return Number(normaliseEntry(this.selectedEntry, TEN_SCALE)[key]) || 0;
    },
    isCellInteractive(cell) {
      return cell.inMonth && (cell.entry !== null || this.canAdd);
    },
    changeMonth(delta) {
      const next = dayjs()
        .year(this.year)
        .month(this.month)
        .date(1)
        .add(delta, "month");
      this.year = next.year();
      this.month = next.month();
    },
    onCellClick(cell) {
      if (!cell.inMonth) return;
      if (cell.entry !== null) {
        this.selectedEntry = cell.entry;
        this.showDayDetail = true;
        return;
      }
      if (this.canAdd) {
        this.$emit("add-day", {
          date: cell.date.toDate(),
          id: null,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.wellness-cal {
  margin-top: 1.5rem;
  border-top: 1px solid #c3cdd5;
  padding-top: 1.25rem;
}

.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.cal-title {
  font-size: 1.1rem;
  margin: 0;
}

.cal-nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cal-nav-button {
  background: #fff;
  border: 1px solid #c3cdd5;
  border-radius: 4px;
  padding: 0.3rem 0.65rem;
  cursor: pointer;
  line-height: 1;
  color: #35424c;

  i {
    color: #35424c;
    font-size: 1rem;
  }

  &:hover,
  &:focus-visible {
    background: #dbe4ea;
    border-color: #9fb0bc;

    i {
      color: #1d2830;
    }
  }
}

.cal-month {
  font-weight: 700;
  min-width: 10.5rem;
  text-align: center;
}

.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 2px;
}

.cal-dow {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5b6770;
  padding: 0.35rem 0.25rem;
  text-align: center;
}

.cal-cell {
  min-height: 3.75rem;
  border: 1px solid #dfe6ea;
  border-radius: 4px;
  padding: 0.3rem 0.4rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  background: #fff;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border-color: #7fa8c9;
    background: #f4f8fb;

    .cal-add {
      opacity: 1;
    }
  }

  &.cal-cell--blank {
    background: transparent;
    border-color: transparent;
    cursor: default;
  }

  &.cal-cell--readonly {
    cursor: default;

    &:hover {
      border-color: #dfe6ea;
      background: #fff;
    }
  }

  &.cal-cell--today {
    border-color: #3273dc;
    box-shadow: inset 0 0 0 1px #3273dc;
  }

  &.cal-cell--has-data {
    background: #f7fbf8;
  }
}

.cal-date {
  font-size: 0.75rem;
  color: #5b6770;
}

.cal-score {
  font-weight: 700;
  font-size: 0.9rem;
  padding: 1px 8px;
  border-radius: 999px;
}

.score--good {
  background: #e6f4ea;
  color: #1d7a3a;
}

.score--mid {
  background: #fdf3df;
  color: #8a5c00;
}

.score--low {
  background: #fdecee;
  color: #c03540;
}

.cal-add {
  opacity: 0;
  color: #7fa8c9;
  font-size: 1.1rem;
  margin: auto;
}

.cal-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.75rem;

  .legend-chip {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 999px;
  }

  .legend-note {
    font-size: 0.75rem;
    color: #5b6770;
  }
}

.day-scores {
  list-style: none;
  margin: 0;
  padding: 0;
}

.day-score {
  display: grid;
  grid-template-columns: 9rem 1fr 3.5rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eef1f3;

  &:last-child {
    border-bottom: none;
  }
}

.day-score__label {
  font-weight: 600;
}

.day-score__bar {
  background: #eef1f3;
  border-radius: 999px;
  height: 0.6rem;
  overflow: hidden;
}

.day-score__fill {
  display: block;
  height: 100%;
  border-radius: 999px;

  &.fill--good {
    background: #2e9e53;
  }

  &.fill--mid {
    background: #e0a52e;
  }

  &.fill--low {
    background: #d85c64;
  }
}

.day-score__value {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

@media screen and (max-width: 640px) {
  .cal-cell {
    min-height: 3rem;
    padding: 0.2rem;
  }

  .cal-score {
    font-size: 0.75rem;
    padding: 1px 5px;
  }
}
</style>
